import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { PageHeader } from "../components/TaskTable";
import { useData } from "../lib/DataContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, Layers, Star, Eye, EyeOff, Pencil, Trash2, RotateCcw, Share2, ChevronUp, ChevronDown, X, Settings, Search } from "lucide-react";

export default function Workspaces() {
  const { favourites, toggleFavourite } = useData();
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const [trash, setTrash] = useState(false);
  const [showFav, setShowFav] = useState(false);
  const [modal, setModal] = useState(null);
  const [shareWs, setShareWs] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/workspaces", { params: { search: search || undefined, show_hidden: showHidden, trash, favourite: showFav } });
    setList(data);
  }, [search, showHidden, trash, showFav]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[idx], next[j]] = [next[j], next[idx]];
    setList(next);
    try { await api.post("/workspaces/reorder", { ordered_ids: next.map((w) => w.id) }); }
    catch { toast.error("Reorder failed"); load(); }
  };

  return (
    <div className="p-4 sm:p-6" data-testid="workspaces-page">
      <PageHeader title="Workspaces" titleClass="text-[28px] leading-9 font-semibold tracking-tight text-slate-900" subtitle={`${list.length} ${trash ? "in trash" : "workspaces"}`} headerExtra={
        <div className="relative flex-1 min-w-40">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input className="h-9 w-full rounded-lg text-sm pl-9" data-testid="ws-search" placeholder="Search workspaces…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      }>
        <button data-testid="ws-filter-fav" onClick={() => setShowFav((s) => !s)} className={`inline-flex items-center gap-2 h-9 px-3.5 rounded-lg border text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${showFav ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}><Star size={15} className={showFav ? "fill-amber-500 text-amber-500" : ""} /> Favourites</button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="ws-settings-menu" title="Settings" className={`inline-flex items-center justify-center h-9 w-9 rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${showHidden || trash ? "bg-slate-800 border-slate-800 text-white" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}><Settings size={16} /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem data-testid="new-workspace-button" onClick={() => setModal({})}><Plus size={15} className="mr-2" /> New Workspace</DropdownMenuItem>
            <DropdownMenuItem data-testid="ws-from-projects" onClick={async () => {
              try { const { data } = await api.post("/workspaces/from-projects"); toast.success(`Created ${data.created} workspace(s) from projects`); load(); }
              catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
            }}><Layers size={15} className="mr-2" /> Add all projects</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="ws-show-hidden" onClick={() => setShowHidden((s) => !s)}><Eye size={15} className="mr-2" /> {showHidden ? "Hide hidden workspaces" : "Show Hidden"}{showHidden && <span className="ml-auto text-[10px] font-semibold text-blue-600 bg-blue-50 rounded px-1.5 py-0.5">ON</span>}</DropdownMenuItem>
            <DropdownMenuItem data-testid="ws-trash" onClick={() => setTrash((s) => !s)}><Trash2 size={15} className="mr-2" /> {trash ? "Exit Trash" : "Trash"}{trash && <span className="ml-auto text-[10px] font-semibold text-red-600 bg-red-50 rounded px-1.5 py-0.5">ON</span>}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {list.map((w, idx) => (
          <div
            key={w.id}
            data-testid={`ws-card-${w.human_id}`}
            role={trash ? undefined : "button"}
            tabIndex={trash ? undefined : 0}
            onClick={(e) => { if (trash) return; if (!e.currentTarget.contains(e.target)) return; nav(`/workspaces/${w.id}`); }}
            onKeyDown={(e) => { if (!trash && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); nav(`/workspaces/${w.id}`); } }}
            className={`bg-white border border-slate-200 rounded-xl px-4 py-4 shadow-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${trash ? "" : "cursor-pointer hover:shadow-md hover:border-slate-300"}`}
          >
            <div className="flex items-start gap-2.5">
              <button data-testid={`ws-fav-${w.human_id}`} onClick={(e) => { e.stopPropagation(); act(() => toggleFavourite("workspace", w.id), "Updated"); }} className={`mt-0.5 h-7 w-7 inline-flex items-center justify-center rounded-md shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${favourites.workspace?.has?.(w.id) ? "text-amber-500" : "text-slate-300 hover:text-amber-400 hover:bg-amber-50"}`}><Star size={15} className={favourites.workspace?.has?.(w.id) ? "fill-amber-500" : ""} /></button>
              <div className="h-9 w-9 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0"><Layers size={18} className="text-indigo-600" /></div>
              <div className="min-w-0 flex-1">
                <div className="tm-heading text-[15px] font-semibold text-slate-900 truncate flex items-center gap-1.5" title={w.name}>
                  {w.is_hidden && <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-slate-500 bg-slate-100 rounded px-1 py-0.5"><EyeOff size={10} /> Hidden</span>}
                  {w.name}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 mt-1">
                <span className={`tm-badge inline-block text-[10px] font-semibold rounded-full px-2 py-0.5 ${w.status === "Active" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{w.status}</span>
                {!trash && <span className="text-slate-300 select-none" aria-hidden="true">|</span>}
                {!trash && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`ws-more-${w.human_id}`} title="Workspace settings" onClick={(e) => e.stopPropagation()} className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"><Settings size={15} /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem data-testid={`ws-share-${w.human_id}`} onClick={(e) => { e.stopPropagation(); setShareWs(w); }}><Share2 size={14} className="mr-2" /> Share</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`ws-rename-${w.human_id}`} onClick={(e) => { e.stopPropagation(); setModal(w); }}><Pencil size={14} className="mr-2" /> Rename</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`ws-hide-${w.human_id}`} onClick={(e) => { e.stopPropagation(); act(() => api.post(`/workspaces/${w.id}/${w.is_hidden ? "unhide" : "hide"}`), "Updated"); }}>{w.is_hidden ? <Eye size={14} className="mr-2" /> : <EyeOff size={14} className="mr-2" />} {w.is_hidden ? "Unhide" : "Hide"}</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem data-testid={`ws-moveup-${w.human_id}`} disabled={idx === 0} onClick={(e) => { e.stopPropagation(); move(idx, -1); }}><ChevronUp size={14} className="mr-2" /> Move up</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`ws-movedown-${w.human_id}`} disabled={idx === list.length - 1} onClick={(e) => { e.stopPropagation(); move(idx, 1); }}><ChevronDown size={14} className="mr-2" /> Move down</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem data-testid={`ws-delete-${w.human_id}`} className="text-red-600 focus:text-red-600" onClick={(e) => { e.stopPropagation(); act(() => api.delete(`/workspaces/${w.id}`), "Moved to trash"); }}><Trash2 size={14} className="mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
            <div className="grid grid-cols-5 gap-1 mt-4 text-center">
              {[["Tasks", w.counts.tasks], ["Notes", w.counts.notes], ["MOM", w.counts.mom], ["Docs", w.counts.documents], ["Collab", w.counts.collaborations]].map(([l, v]) => (
                <div key={l}><div className="text-base font-semibold text-slate-900 leading-5">{v}</div><div className="tm-body text-[10px] text-slate-500 mt-0.5">{l}</div></div>
              ))}
            </div>
            {trash && (
              <div className="flex items-center justify-end mt-4 border-t border-slate-100 pt-3">
                <Button variant="outline" size="sm" data-testid={`ws-restore-${w.human_id}`} onClick={(e) => { e.stopPropagation(); act(() => api.post(`/workspaces/${w.id}/restore`), "Restored"); }}><RotateCcw size={14} className="mr-1" /> Restore</Button>
              </div>
            )}
          </div>
        ))}
        {list.length === 0 && <div className="text-sm text-slate-400 col-span-full py-10 text-center">No workspaces {trash ? "in trash" : "yet"}.</div>}
      </div>

      {modal && <WsModal ws={modal.id ? modal : null} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {shareWs && <WsShareModal ws={shareWs} onClose={() => setShareWs(null)} onChange={load} />}
    </div>
  );
}

function WsShareModal({ ws, onClose, onChange }) {
  const { users } = useData();
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ user_id: "", ws_role: "editor" });
  const load = useCallback(() => api.get(`/workspaces/${ws.id}/members`).then((r) => setMembers(r.data)).catch(() => {}), [ws.id]);
  useEffect(() => { load(); }, [load]);
  const share = async () => {
    if (!form.user_id) return toast.error("Select a user");
    try { await api.post(`/workspaces/${ws.id}/members`, form); toast.success("Workspace shared"); setForm({ user_id: "", ws_role: "editor" }); load(); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (uid) => { await api.delete(`/workspaces/${ws.id}/members/${uid}`); toast.success("Access removed"); load(); onChange?.(); };
  const roles = ["viewer", "contributor", "editor", "admin"];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="ws-share-modal">
        <DialogHeader><DialogTitle>Share: {ws.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-400">Share this workspace with other users. Sharing does not change ownership.</p>
          <div className="flex gap-2">
            <select className="flex-1 border rounded-lg h-10 px-2 text-sm" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} data-testid="ws-share-user">
              <option value="">Select user…</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.full_name} ({u.email})</option>)}
            </select>
            <select className="border rounded-lg h-10 px-2 text-sm" value={form.ws_role} onChange={(e) => setForm({ ...form, ws_role: e.target.value })} data-testid="ws-share-role">
              {roles.map((r) => <option key={r} value={r} className="capitalize">{r}</option>)}
            </select>
          </div>
          <Button className="w-full" onClick={share} data-testid="ws-share-submit"><Share2 size={14} className="mr-1.5" />Share</Button>
          <div className="border-t pt-3 space-y-2 max-h-56 overflow-auto">
            <div className="text-xs font-semibold text-slate-500">Members ({members.length})</div>
            {members.length === 0 && <div className="text-sm text-slate-400">No additional members yet.</div>}
            {members.map((m) => (
              <div key={m.id} data-testid={`ws-member-${m.user_id}`} className="flex items-center gap-2 text-sm border rounded-lg p-2">
                <div className="min-w-0 flex-1"><div className="truncate text-slate-700">{m.user_name}</div><div className="text-[11px] text-slate-400 capitalize">{m.ws_role}</div></div>
                <button onClick={() => remove(m.user_id)} data-testid={`ws-member-remove-${m.user_id}`} className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"><X size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WsModal({ ws, onClose, onDone }) {
  const { projects } = useData();
  const [f, setF] = useState({ name: ws?.name || "", description: ws?.description || "", status: ws?.status || "Active", start_date: ws?.start_date || "", target_date: ws?.target_date || "", project_id: ws?.project_id || "" });
  const submit = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    try {
      const body = { ...f, project_id: f.project_id && f.project_id !== "none" ? f.project_id : null };
      if (ws) await api.put(`/workspaces/${ws.id}`, body);
      else await api.post("/workspaces", body);
      toast.success(ws ? "Workspace updated" : "Workspace created"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="ws-modal">
        <DialogHeader><DialogTitle>{ws ? "Edit Workspace" : "New Workspace"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input data-testid="ws-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><Label>Description</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start Date</Label><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></div>
            <div><Label>Target Date</Label><Input type="date" value={f.target_date} onChange={(e) => setF({ ...f, target_date: e.target.value })} /></div>
          </div>
          <div><Label>Related Project (optional)</Label>
            <select data-testid="ws-project-select" value={f.project_id || "none"} onChange={(e) => setF({ ...f, project_id: e.target.value })}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
              <option value="none">— No project —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} data-testid="ws-submit">{ws ? "Save" : "Create"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
